import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AiOutlinePlus } from 'react-icons/ai';
import PageHero from '../components/PageHero';

const FAQ_DATA = [
  {
    question: "What types of events do you provide decorations for?",
    answer: "We provide balloon decorations for a wide range of events including birthdays, anniversaries, baby showers, engagements, weddings, corporate events, and themed parties. Our decorations can be customized to suit any celebration."
  },
  {
    question: "Do you offer customized decoration packages?",
    answer: "Yes, we offer fully customizable decoration packages to suit your specific event needs, theme preferences, and budget. Our team will work with you to create the perfect decoration setup for your special occasion."
  },
  {
    question: "How far in advance should I book your decoration services?",
    answer: "We recommend booking at least 1-2 weeks in advance for standard decorations and 3-4 weeks for complex or large-scale events. However, we also try to accommodate last-minute requests whenever possible."
  },
  {
    question: "What is your refund policy for cancellations?",
    answer: "Full refund of the amount will be given if the booking is cancelled at least 72 hours before the scheduled time. For cancellations made less than 72 hours before, a 50% refund will be provided."
  },
  {
    question: "Do you provide decoration services outside of Bangalore?",
    answer: "Currently, we primarily serve the Bangalore area. For locations outside the city, there may be additional travel charges depending on the distance. Please contact us for more information on out-of-city services."
  },
  {
    question: "How long does it take to set up the decorations?",
    answer: "Setup time varies depending on the complexity and scale of the decoration. Simple setups may take 1-2 hours, while elaborate ones can take 3-5 hours. We always ensure the decorations are ready before your event starts."
  }
];

const About = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  return (
    <div>
      <PageHero 
        title="About Us"
        subtitle="We are the Premium Balloon Decoration Service in Bangalore providing Magical Celebration Experiences"
        image="/gallery/image1.jpg"
        buttons={[
          { to: "/", label: "Home" },
          { to: "/services", label: "Our Services" }
        ]}
      />

      {/* Main Content Section */}
      <section className="pt-6 pb-20 bg-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-center mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Premium Balloon Decorations & <br /> Celebration Services
          </motion.h2>
          <motion.p 
            className="text-center mt-5 text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            We take pride in being the ultimate balloon decoration service, beautifully designed to make your special moments truly extraordinary. From colorful birthday celebrations to elegant wedding setups, romantic anniversary surprises, adorable baby showers, and corporate events, our decorations are tailored to suit every occasion. With stunning balloon arrangements, innovative designs, and attention to detail, we create an ambiance that blends joy, style, and creativity for your perfect celebration.
          </motion.p>

          <div className="flex gap-6 justify-center mt-12">
            <motion.div 
              className="relative hidden md:block w-1/4 aspect-[3/4] rounded-2xl overflow-hidden shadow-xl"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                className="absolute w-full h-full object-cover"
                src="/images/image2.jpg"
                alt="birthday decoration"
              />
            </motion.div>
            <motion.div 
              className="relative w-full md:w-2/4 aspect-[16/9] rounded-2xl overflow-hidden shadow-xl md:-mb-8 md:mt-8"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <img 
                className="absolute w-full h-full object-cover"
                src="/gallery/image2.jpg"
                alt="balloon decoration"
              />
            </motion.div>
            <motion.div 
              className="relative hidden md:block w-1/4 aspect-[3/4] rounded-2xl overflow-hidden shadow-xl"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                className="absolute w-full h-full object-cover"
                src="/images/image.jpg"
                alt="anniversary decoration"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-4 flex flex-col md:flex-row items-center gap-12">
          <motion.div 
            className="w-full md:w-1/2"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8">Our Brand Story</h2>
            <div className="space-y-4 text-gray-300">
              <p>In the heart of Bangalore, Habba Events was created with a vision to provide extraordinary decoration services that combine creativity with elegance.</p>
              <p>We offer imaginative, high-quality balloon decorations for birthdays, anniversaries, and all special occasions, designed to create unforgettable memories. Our brand quickly became a favorite for those seeking personalized celebration experiences.</p>
              <p>With a focus on customer satisfaction, each decoration combines artistic flair and attention to detail, offering unique setups for gatherings of any size. Whether it's an intimate family event or a grand celebration, we set the perfect atmosphere with our balloon decorations.</p>
            </div>
          </motion.div>
          <motion.div 
            className="w-full md:w-1/2"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img 
              src="/gallery/image3.jpg"
              alt="Brand Story"
              className="rounded-2xl shadow-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {FAQ_DATA.map((faq, index) => (
              <motion.div 
                key={index}
                className="border border-gray-300 rounded-lg overflow-hidden bg-white"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <button
                  className="w-full flex justify-between items-center px-6 py-4 text-left"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  <AiOutlinePlus 
                    className={`transform transition-transform ${openFaq === index ? 'rotate-45' : ''}`}
                  />
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out ${
                    openFaq === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="px-6 pb-4 text-gray-600">{faq.answer}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 